import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import PhotoUpload from "./PhotoUpload";
import CommentTimeline from "./CommentTimeline";
import { ArrowLeft, Save, Lock, Plus, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";

const STATUSES = ["Eingetroffen", "In Bearbeitung", "Erneut versendet", "Storniert", "Abgeschlossen"];
const STORAGE_QUICK = "Die Ware wurde zurück ins Lager gelegt.";

function newArticleRow() {
  return { id: `tmp-${Math.random().toString(36).slice(2, 9)}`, article_info: "", quantity: "" };
}

const EMPTY = {
  order_number: "",
  customer_name: "",
  customer_number: "",
  address: "",
  shop_origin: "",
  storage_location: "",
  tracking_id: "",
  status: "Eingetroffen",
};

export default function ReturnForm({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();
  const isNew = mode === "new";

  const [data, setData] = useState(EMPTY);
  const [articles, setArticles] = useState([newArticleRow()]);
  const [shopOrigins, setShopOrigins] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const completed = data.status === "Abgeschlossen";
  const readOnly = !isNew && completed && user?.role !== "admin";

  useEffect(() => {
    api.get("/shop-origins").then((r) => setShopOrigins(r.data)).catch(() => {});
    if (!isNew && id) {
      api
        .get(`/returns/${id}`)
        .then((r) => {
          const d = r.data;
          setData({
            order_number: d.order_number || "",
            customer_name: d.customer_name || "",
            customer_number: d.customer_number || "",
            address: d.address || "",
            shop_origin: d.shop_origin || "",
            storage_location: d.storage_location || "",
            tracking_id: d.tracking_id || "",
            status: d.status || "Eingetroffen",
          });
          const items = (d.articles && d.articles.length > 0) ? d.articles : [newArticleRow()];
          setArticles(items.map((a) => ({
            id: a.id || `tmp-${Math.random().toString(36).slice(2, 9)}`,
            article_info: a.article_info || "",
            quantity: a.quantity || "",
          })));
          setPhotos(d.photos || []);
          setComments(d.comments || []);
        })
        .catch((e) => toast.error(formatApiError(e)))
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const updateArticle = (idx, key, value) => {
    setArticles((rows) => rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };
  const addArticle = () => setArticles((rows) => [...rows, newArticleRow()]);
  const removeArticle = (idx) => {
    setArticles((rows) => {
      const next = rows.filter((_, i) => i !== idx);
      return next.length > 0 ? next : [newArticleRow()];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.order_number.trim() || !data.customer_name.trim()) {
      toast.error("Bestellnummer und Kundenname sind erforderlich");
      return;
    }
    const cleanedArticles = articles
      .map((a) => ({
        id: a.id && !a.id.startsWith("tmp-") ? a.id : undefined,
        article_info: a.article_info.trim(),
        quantity: a.quantity.trim(),
      }))
      .filter((a) => a.article_info || a.quantity);
    const payload = { ...data, articles: cleanedArticles };
    setSaving(true);
    try {
      if (isNew) {
        const { data: created } = await api.post("/returns", payload);
        toast.success(`${created.id} ${t("common.saved")}`);
        navigate(`/returns/${created.id}`);
      } else {
        await api.put(`/returns/${id}`, payload);
        toast.success(t("common.saved"));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/returns/${id}`);
      toast.success(`${id} gelöscht`);
      navigate("/returns");
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground">{t("common.loading")}</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <Link to="/returns" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center mb-4" data-testid="back-to-returns">
        <ArrowLeft className="w-4 h-4 mr-1" /> {t("form.backToList")}
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-none">
            {isNew ? t("dashboard.newReturn") : id}
          </h1>
          {!isNew && <p className="label-mini mt-2">{t("form.title")}</p>}
        </div>
      </div>

      {readOnly && (
        <div className="mb-4 p-3 status-abgeschlossen text-sm flex items-center gap-2" style={{ borderRadius: 4 }} data-testid="return-locked-banner">
          <Lock className="w-4 h-4" /> {t("form.locked")}
        </div>
      )}
      {!isNew && completed && user?.role === "admin" && (
        <div className="mb-4 p-3 status-abgeschlossen text-sm flex items-center gap-2" style={{ borderRadius: 4 }}>
          <Lock className="w-4 h-4" /> {t("form.lockedAdmin")}
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label={t("form.orderNumber")} required>
          <Input value={data.order_number} onChange={(e) => set("order_number", e.target.value)} disabled={readOnly} data-testid="field-order-number" style={{ borderRadius: 4 }} />
        </Field>
        <Field label={t("form.customerName")} required>
          <Input value={data.customer_name} onChange={(e) => set("customer_name", e.target.value)} disabled={readOnly} data-testid="field-customer-name" style={{ borderRadius: 4 }} />
        </Field>
        <Field label={t("form.customerNumber")}>
          <Input value={data.customer_number} onChange={(e) => set("customer_number", e.target.value)} disabled={readOnly} data-testid="field-customer-number" style={{ borderRadius: 4 }} />
        </Field>
        <Field label={t("form.shopOrigin")}>
          <Select value={data.shop_origin || ""} onValueChange={(v) => set("shop_origin", v)} disabled={readOnly}>
            <SelectTrigger data-testid="field-shop-origin" style={{ borderRadius: 4 }}>
              <SelectValue placeholder={t("form.selectShop")} />
            </SelectTrigger>
            <SelectContent>
              {shopOrigins.map((s) => (
                <SelectItem key={s.id} value={s.name} data-testid={`shop-option-${s.name}`}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("form.address")} className="md:col-span-2">
          <Textarea value={data.address} onChange={(e) => set("address", e.target.value)} disabled={readOnly} rows={2} data-testid="field-address" style={{ borderRadius: 4 }} />
        </Field>

        {/* Articles list */}
        <div className="md:col-span-2">
          <div className="flex items-baseline justify-between mb-2">
            <Label className="label-mini">{t("form.articlesLabel")}</Label>
            <span className="text-xs text-muted-foreground">{articles.length} {articles.length === 1 ? t("form.articleSingular") : t("form.articlePlural")}</span>
          </div>
          <div className="space-y-3" data-testid="articles-list">
            {articles.map((a, idx) => (
              <div
                key={a.id}
                className="swiss-card p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-start"
                data-testid={`article-row-${idx}`}
              >
                <div className="sm:col-span-1 flex sm:flex-col items-center justify-between sm:justify-start sm:items-start gap-2">
                  <span className="font-mono text-xs text-muted-foreground">#{idx + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeArticle(idx)}
                    disabled={readOnly || (articles.length === 1 && !a.article_info && !a.quantity)}
                    className="sm:mt-1 text-muted-foreground hover:text-destructive disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label={t("form.removeArticle")}
                    data-testid={`article-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="sm:col-span-5">
                  <Label className="label-mini mb-1 block">{t("form.articleInfo")}</Label>
                  <Input
                    value={a.article_info}
                    onChange={(e) => updateArticle(idx, "article_info", e.target.value)}
                    disabled={readOnly}
                    placeholder="PZN / Art.-Nr. / Name"
                    data-testid={`article-info-${idx}`}
                    style={{ borderRadius: 4 }}
                  />
                </div>
                <div className="sm:col-span-6">
                  <Label className="label-mini mb-1 block">{t("form.quantity")}</Label>
                  <Textarea
                    value={a.quantity}
                    onChange={(e) => updateArticle(idx, "quantity", e.target.value)}
                    disabled={readOnly}
                    rows={2}
                    placeholder="z.B. 2 Stück, beschädigte Verpackung"
                    data-testid={`article-quantity-${idx}`}
                    style={{ borderRadius: 4 }}
                  />
                </div>
              </div>
            ))}
          </div>
          {!readOnly && (
            <Button
              type="button"
              variant="outline"
              onClick={addArticle}
              className="mt-3"
              style={{ borderRadius: 4 }}
              data-testid="article-add-button"
            >
              <Plus className="w-4 h-4 mr-2" /> {t("form.addArticle")}
            </Button>
          )}
        </div>

        <Field label={t("form.storage")} className="md:col-span-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={data.storage_location} onChange={(e) => set("storage_location", e.target.value)} disabled={readOnly} className="flex-1" data-testid="field-storage" style={{ borderRadius: 4 }} />
            <Button
              type="button"
              variant="outline"
              onClick={() => set("storage_location", STORAGE_QUICK)}
              disabled={readOnly}
              style={{ borderRadius: 4 }}
              data-testid="field-storage-quick"
            >
              {t("form.storageQuick")}
            </Button>
          </div>
        </Field>
        <Field label={t("form.tracking")}>
          <Input value={data.tracking_id} onChange={(e) => set("tracking_id", e.target.value)} disabled={readOnly} data-testid="field-tracking" style={{ borderRadius: 4 }} />
        </Field>
        <Field label={t("form.status")}>
          <Select value={data.status} onValueChange={(v) => set("status", v)} disabled={readOnly}>
            <SelectTrigger data-testid="field-status" style={{ borderRadius: 4 }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>
                  {t(`stats.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="md:col-span-2 flex flex-col sm:flex-row gap-2 mt-2">
          <Button
            type="submit"
            disabled={saving || readOnly}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
            style={{ borderRadius: 4 }}
            data-testid="return-submit-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {isNew ? t("form.saveNew") : t("form.save")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/returns")}
            style={{ borderRadius: 4 }}
            data-testid="return-cancel-button"
          >
            {t("form.cancel")}
          </Button>

          {!isNew && user?.role === "admin" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="border-destructive text-destructive hover:bg-red-50 sm:ml-auto"
                  style={{ borderRadius: 4 }}
                  data-testid="return-delete-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> {t("actions.delete")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent style={{ borderRadius: 4 }} data-testid="return-delete-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("actions.deleteConfirmTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("actions.deleteConfirmDesc")} <span className="font-mono font-semibold">{id}</span> — {data.customer_name}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel style={{ borderRadius: 4 }} data-testid="return-delete-cancel">
                    {t("form.cancel")}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    disabled={deleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    style={{ borderRadius: 4 }}
                    data-testid="return-delete-confirm"
                  >
                    {deleting ? t("common.loading") : t("actions.delete")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </form>

      {!isNew && (
        <>
          <div className="mt-10">
            <h2 className="text-xl font-bold tracking-tight mb-3">{t("form.photos")}</h2>
            <PhotoUpload returnId={id} photos={photos} onChange={setPhotos} disabled={readOnly} />
          </div>
          <div className="mt-10">
            <h2 className="text-xl font-bold tracking-tight mb-3">{t("form.comments")}</h2>
            <CommentTimeline returnId={id} comments={comments} onChange={setComments} disabled={readOnly} />
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="label-mini mb-1.5 block">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}
